import { RoundtripOfferViewModel } from '../types/offer.types';

export interface SchemaEventPayload {
    uri: string;
    data: RoundtripOfferViewModel;
}

const SCHEMA_EVENT = 'der-tm:schema';
const URI_DOMAIN = 'der:tailormade';
const URI_CONTEXT = 'roundtrip';
const URI_DOMAIN_SEGMENTS = URI_DOMAIN.split(':').filter(Boolean);
const MFE_NAME = String(import.meta.env?.VITE_MFE_NAME ?? 'der-tm-roundtrip-offer-panel').replace('@dertour/', '');
const RETRY_DELAY_MS = 500;
const MAX_RETRIES = 10;

/**
 * Checks if the external schema injector script has signaled readiness.
 * @returns Whether the global flag is set to true
 */
function isSchemaOrgListenerReady(): boolean {
    return (window as { schemaInjectorReady?: boolean }).schemaInjectorReady === true;
}

/**
 * Dispatches the schema custom event on the given target.
 * @param payload - Event detail containing URI and image data
 * @param target - DOM target to dispatch from
 */
function dispatchSchemaEvent(payload: SchemaEventPayload, target: EventTarget): void {
    target.dispatchEvent(
        new CustomEvent<SchemaEventPayload>(SCHEMA_EVENT, {
            detail: payload,
            bubbles: true,
            composed: true,
        })
    );
}

/**
 * Dispatches the schema event, retrying until the injector is ready (max ~5s).
 * @param payload - Event detail containing URI and image data
 * @param dispatchTarget - DOM target to dispatch from
 */
export function triggerSchemaJsonEvent(payload: SchemaEventPayload, dispatchTarget: EventTarget = window): void {
    if (isSchemaOrgListenerReady()) {
        dispatchSchemaEvent(payload, dispatchTarget);
        return;
    }

    let attempts = 0;

    const retry = () => {
        if (isSchemaOrgListenerReady()) {
            dispatchSchemaEvent(payload, dispatchTarget);
            return;
        }

        if (++attempts < MAX_RETRIES) {
            setTimeout(retry, RETRY_DELAY_MS);
        }
    };

    setTimeout(retry, RETRY_DELAY_MS);
}

/**
 * Intercepts schema events from child elements, rewrites the URI, and re-dispatches from the host.
 * @param element - Host element to attach the listener to
 * @returns Cleanup function that removes the event listener
 */
export function interceptChildEvents(element: HTMLElement): () => void {
    const handler = (event: Event) => {
        const customEvent = event as CustomEvent<SchemaEventPayload>;

        if (event.composedPath()[0] === element) {
            return;
        }

        const uri = updateUri(customEvent.detail.uri);

        if (!uri) {
            return;
        }

        event.stopPropagation();

        element.dispatchEvent(
            new CustomEvent<SchemaEventPayload>(SCHEMA_EVENT, {
                detail: {
                    ...customEvent.detail,
                    uri,
                },
                bubbles: true,
                composed: true,
            })
        );
    };

    element.addEventListener(SCHEMA_EVENT, handler);

    return () => {
        element.removeEventListener(SCHEMA_EVENT, handler);
    };
}

/**
 * Builds the initial colon-separated URI for the given identifier.
 * @param id - Roundtrip identifier
 * @returns Fully qualified schema URI
 */
export function setInitialUri(id: string): string {
    console.log('Identifier: ', id);
    return `${URI_DOMAIN}:${URI_CONTEXT}:${MFE_NAME}:${id}`;
}

/**
 * Rewrites an incoming URI to include this MFE's context segment. Returns null if the URI is foreign.
 * @param uri - The incoming colon-separated URI to rewrite
 * @returns Rewritten URI or null if not applicable
 */
export function updateUri(uri: string): string | null {
    if (!uri.startsWith(URI_DOMAIN)) {
        return null;
    }
    const parts = uri.split(':');
    const id = parts.pop();

    if (!id) {
        return null;
    }

    parts.splice(0, URI_DOMAIN_SEGMENTS.length);

    const firstSegment = parts[0];

    if (!firstSegment) {
        return null;
    }

    const hasCapabilityContext = firstSegment.startsWith('der-tm');

    const context = hasCapabilityContext ? firstSegment : URI_CONTEXT;

    const remainingSegments = hasCapabilityContext ? parts.slice(1) : parts;

    return [...URI_DOMAIN_SEGMENTS, context, MFE_NAME, ...remainingSegments, id].join(':');
}
